import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Btn, H3, LI, UL } from '../../../../../../AbstractElements'; // Adjust the import path accordingly
import { AssetsPropertyFormData, AssetsPropertyFormErrors } from '../../../FormData';
import { Button, Col, Form, Row } from 'reactstrap';
import TextField from '../../../../../common/TextField';
import DatePicker from '../../../../../common/DatePicker';
import DropDown from '../../../../../common/DropDown';
import DollarInput from '../../../../../common/DollarInput';
import DecimalInput from '../../../../../common/DecimalInput';
import { useMutationRequestQuery } from '../../../../../../Hooks/useMutationRequestQuery';
import { getAssetsPropertyByClientIdService, getAssetsPropertyByIdService, insertOrUpdateAssetsPropertyService, softDeleteAssetsPropertyService } from '../../../../../../Services/PublicPageServices';
import { setFormStatus } from '../../../../../../ReduxToolkit/Reducers/FromStatusSlice';
import { useDispatch } from 'react-redux';
import { Continue } from '../../../../../../Utils/Constants';
import { setFormsData } from '../../../../../../ReduxToolkit/Reducers/FormDataSlice';
import { useGetRequestQuery } from '../../../../../../Hooks/useGetRequestQuery';
import DataTable from 'react-data-table-component';
import { replaceFormData } from '../../../../../../ReduxToolkit/Reducers/FactFindDataSlice';

export default function AssetsPropertyForm({ activeCallBack, clientFactFindData, clientId }: any) {
    const [formData, setFormData] = useState<any>(AssetsPropertyFormData);
    const [formErrors, setFormErrors] = useState<any>(AssetsPropertyFormErrors);
    const [assetsPropertyData, setAssetsPropertyData] = useState([]);
    const [selectedRowId, setSelectedRowId] = useState<number | null>(null);

    const dispatch = useDispatch()
    const validatePropertyHomeAddress = (value: string) => value.trim() !== '';
    const validatePropertyEstimatedValue = (value: string) => value.trim() !== '';
    const validatePropertyDatePurchased = (value: string) => value.trim() !== '';
    const validatePropertyType = (value: string) => value.trim() !== '';
    const validatePropertyDetailedType = (value: string) => value.trim() !== '';
    const validatePropertyRentalIncome = (value: string) => value.trim() !== '';
    const validateLoanDetails = (value: string) => value.trim() !== '';


    const {
        data: assetsProperty,
        refetch: refetchAssetsProperty,
        isLoading: assetsPropertyLoading,
        isError: assetsPropertyIsError,
    } = useGetRequestQuery(
        ['getAssetsPropertyByClientId', clientId],
        () => getAssetsPropertyByClientIdService(clientId),
        {
            enabled: true, // enable query only if clientId is available
        }
    );

    const {
        data: assetsPropertyFormData,
        isLoading: assetsPropertyFormDataLoading,
        isError: assetsPropertyFormDataIsError,
    } = useGetRequestQuery(
        ["getAssetsPropertyById", selectedRowId],
        () => getAssetsPropertyByIdService(selectedRowId),
        {
            enabled: !!selectedRowId,
        }
    );

    useEffect(() => {
        if (assetsProperty?.data && !assetsPropertyIsError && !assetsPropertyLoading) {
            setAssetsPropertyData(assetsProperty?.data);
            dispatch(
                replaceFormData({
                    formName: "assetsProperty",
                    formData: assetsProperty?.data,
                })
            );
        }
    }, [assetsProperty, assetsPropertyIsError, assetsPropertyLoading]);


    useEffect(() => {
        if (assetsPropertyFormData?.data && !assetsPropertyFormDataIsError && !assetsPropertyFormDataLoading) {
            if (assetsPropertyFormData?.data?.id) {
                setSelectedRowId(assetsPropertyFormData?.data?.id)
            }
            setFormData({
                propertyHomeAddress: assetsPropertyFormData?.data?.homeAddress,
                propertyEstimatedValue: assetsPropertyFormData?.data?.estimatedValue,
                propertyDatePurchased: assetsPropertyFormData?.data?.datePurchased,
                propertyType: assetsPropertyFormData?.data?.propertyType,
                propertyDetailedType: assetsPropertyFormData?.data?.detailedType,
                propertyRentalIncome: assetsPropertyFormData?.data?.rentalIncome,
                propertyRentalIncomeDLL: assetsPropertyFormData?.data?.frequency,
                loanDetailsBorrowTypeDLL: assetsPropertyFormData?.data?.borrowType,
                loanDetailsCurrentMortgage: assetsPropertyFormData?.data?.currentMortgage,
                loanDetailsLendersName: assetsPropertyFormData?.data?.lendersName,
                loanDetailsRepayType: assetsPropertyFormData?.data?.repayType,
                loanDetailsRepayment: assetsPropertyFormData?.data?.loanDetailsRepayment,
                loanDetailsRepaymentDLL: assetsPropertyFormData?.data?.loanFrequency,
                loanDetailsInterestRate: assetsPropertyFormData?.data?.interestRate,
                loanDetailsInterestType: assetsPropertyFormData?.data?.interestType,
            })

        }
    }, [assetsPropertyFormData, assetsPropertyFormDataIsError, assetsPropertyFormDataLoading]);


    const { mutate: updateClientAssets, isPending: isUpdateClientAssetsPending } = useMutationRequestQuery(
        'insertOrUpdateAssetsPropertyService',
        insertOrUpdateAssetsPropertyService,
        {
            onSuccess: () => {
                toast.success("Assets property information saved or updated successfully.");
                dispatch(setFormStatus({ id: '4', status: 4 }));
                // setFormErrors(AssetsPropertyFormErrors);
                setFormData(AssetsPropertyFormData)
                setSelectedRowId(null)
                refetchAssetsProperty()
            },
            onError: (error: any) => {
                toast.error("Failed to submit assets property information");
                console.error(error);
            },
        }
    );

    const {
        mutate: deleteClientAssets,
    } = useMutationRequestQuery(
        'softDeleteAssetsPropertyService',
        softDeleteAssetsPropertyService,
        {
            onSuccess: () => {
                toast.success("Property deleted successfully.");
                refetchAssetsProperty()
                setSelectedRowId(null)
            },
            onError: (error: any) => {
                toast.error("Failed to delete property.");
                console.error(error);
            },
        }
    );

    useEffect(() => {
        dispatch(setFormsData({
            id: '4',
            formData: formData,
        }));
    }, [formData])


    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const propertyHomeAddressValid = validatePropertyHomeAddress(formData.propertyHomeAddress);
        const propertyEstimatedValueValid = validatePropertyEstimatedValue(formData.propertyEstimatedValue);
        const propertyDatePurchasedValid = validatePropertyDatePurchased(formData.propertyDatePurchased);
        const propertyTypeValid = validatePropertyType(formData.propertyType);
        const propertyDetailedTypeValid = validatePropertyDetailedType(formData.propertyDetailedType);
        const propertyRentalIncomeValid = validatePropertyRentalIncome(formData.propertyRentalIncome);
        const propertyRentalIncomeDLLValid = validatePropertyRentalIncome(formData.propertyRentalIncomeDLL);
        const loanDetailsBorrowTypeDLLValid = validateLoanDetails(formData.loanDetailsBorrowTypeDLL);
        const loanDetailsCurrentMortgageValid = validateLoanDetails(formData.loanDetailsCurrentMortgage);
        const loanDetailsLendersNameValid = validateLoanDetails(formData.loanDetailsLendersName);
        const loanDetailsRepayTypeValid = validateLoanDetails(formData.loanDetailsRepayType);
        const loanDetailsRepaymentValid = validateLoanDetails(formData.loanDetailsRepayment);
        const loanDetailsRepaymentDLLValid = validateLoanDetails(formData.loanDetailsRepaymentDLL);
        const loanDetailsInterestRateValid = validateLoanDetails(formData.loanDetailsInterestRate);
        const loanDetailsInterestTypeValid = validateLoanDetails(formData.loanDetailsInterestType);

        setFormErrors({
            propertyHomeAddress: !propertyHomeAddressValid,
            propertyEstimatedValue: !propertyEstimatedValueValid,
            propertyDatePurchased: !propertyDatePurchasedValid,
            propertyType: !propertyTypeValid,
            propertyDetailedType: !propertyDetailedTypeValid,
            propertyRentalIncome: !propertyRentalIncomeValid,
            propertyRentalIncomeDLL: !propertyRentalIncomeDLLValid,
            loanDetailsBorrowTypeDLL: !loanDetailsBorrowTypeDLLValid,
            loanDetailsCurrentMortgage: !loanDetailsCurrentMortgageValid,
            loanDetailsLendersName: !loanDetailsLendersNameValid,
            loanDetailsRepayType: !loanDetailsRepayTypeValid,
            loanDetailsRepayment: !loanDetailsRepaymentValid,
            loanDetailsRepaymentDLL: !loanDetailsRepaymentDLLValid,
            loanDetailsInterestRate: !loanDetailsInterestRateValid,
            loanDetailsInterestType: !loanDetailsInterestTypeValid,
        });

        const isValid =
            propertyHomeAddressValid &&
            propertyEstimatedValueValid &&
            propertyDatePurchasedValid &&
            propertyTypeValid &&
            propertyDetailedTypeValid &&
            propertyRentalIncomeValid &&
            propertyRentalIncomeDLLValid &&
            loanDetailsBorrowTypeDLLValid &&
            loanDetailsCurrentMortgageValid &&
            loanDetailsLendersNameValid &&
            loanDetailsRepayTypeValid &&
            loanDetailsRepaymentValid &&
            loanDetailsRepaymentDLLValid &&
            loanDetailsInterestRateValid &&
            loanDetailsInterestTypeValid;
        if (!isValid) {
            return;
        } else if (clientId != 0) {
            updateClientAssets(
                {
                    clientID: clientId,
                    id: selectedRowId ? selectedRowId : '0',
                    homeAddress: formData.propertyHomeAddress,
                    estimatedValue: formData.propertyEstimatedValue,
                    datePurchased: formData.propertyDatePurchased,
                    propertyType: formData.propertyType,
                    detailedType: formData.propertyDetailedType,
                    rentalIncome: formData.propertyRentalIncome,
                    frequency: formData.propertyRentalIncomeDLL,
                    borrowType: formData.loanDetailsBorrowTypeDLL,
                    currentMortgage: formData.loanDetailsCurrentMortgage,
                    lendersName: formData.loanDetailsLendersName,
                    repayType: formData.loanDetailsRepayType,
                    loanDetailsRepayment: formData.loanDetailsRepayment,
                    loanFrequency: formData.loanDetailsRepaymentDLL,
                    interestRate: formData.loanDetailsInterestRate,
                    interestType: formData.loanDetailsInterestType,
                }
            )
        }
    };

    // Handle field value change
    const handleValueChange = (name: string, value: string) => {
        setFormData((prevData: any) => ({
            ...prevData,
            [name]: value,
        }));
    };
    const handleErrorChange = (name: string, error: boolean) => {
        setFormErrors((prevErrors: any) => ({
            ...prevErrors,
            [name]: error,
        }));
    };

    const splitDropdownValues = (dropdownValues: string) => {
        return dropdownValues ? dropdownValues.split(',').map(value => value.trim()) : [];
    };

    const handleEditClick = (id: number) => {
        setSelectedRowId(id);
    };

    const handleDeleteClick = (id: number) => {
        deleteClientAssets(id)
    };

    const zeroConfigurationTableColumns = [
        {
            name: "Action",
            cell: (row: any) => (
                <UL className="action simple-list flex-row">
                    <LI className="edit"><a onClick={() => { handleEditClick(row.id) }} href="#"><i className="icon-pencil-alt" /></a></LI>
                    <LI className="delete"><a onClick={() => { handleDeleteClick(row.id) }} href="#"><i className="icon-trash" /></a></LI>
                </UL>
            )
        },
        {
            name: "Property Address",
            selector: (row: any) => row["homeAddress"],
            sortable: false,
            width: '30rem',
        },
        {
            name: "Estimated Value",
            selector: (row: any) => row["estimatedValue"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Date Purchased",
            selector: (row: any) => {
                const date = new Date(row["datePurchased"]);
                return date.toLocaleDateString("en-GB"); 
            },
            sortable: false,
            width: '10rem'
        },
        {
            name: "Property Type",
            selector: (row: any) => row["propertyType"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Rental Income",
            selector: (row: any) => row["rentalIncome"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Frequency",
            selector: (row: any) => row["frequency"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Detailed Type",
            selector: (row: any) => row["detailedType"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Borrow Type",
            selector: (row: any) => row["borrowType"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Current Mortgage",
            selector: (row: any) => row["currentMortgage"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Lender's Name",
            selector: (row: any) => row["lendersName"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Repay Type",
            selector: (row: any) => row["repayType"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Repayment",
            selector: (row: any) => row["loanDetailsRepayment"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Repayment Frequency",
            selector: (row: any) => row["loanFrequency"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Interest Rate",
            selector: (row: any) => row["interestRate"],
            sortable: false,
            width: '10rem'
        },
        {
            name: "Interest Type",
            selector: (row: any) => row["interestType"],
            sortable: false,
            width: '10rem'
        },
    ];

    return (
        <>

            <Form
                style={{
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    padding: '30px',
                    backgroundColor: '#fff',
                }}
                onSubmit={handleSubmit}
            >
                {assetsPropertyData.length !== 0 ? (
                    <div
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            padding: '5px',
                            backgroundColor: '#fff',
                        }}
                    >
                        <Row className='g-1 m-2'>
                            <Col xs={12} className='text-left'>
                                <H3 className='f-w-900 heading-color'>Current Properties</H3>
                            </Col>
                        </Row>
                        <DataTable
                            title=""
                            columns={zeroConfigurationTableColumns}
                            data={assetsPropertyData}
                            // pagination
                            responsive
                            style={{
                                tableLayout: 'auto',
                            }}
                        />
                    </div>
                ) : <div style={{
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    padding: '15px',
                    backgroundColor: '#fff',
                }}>
                    <Row className='g-3 mb-4'>
                        <Col xs={12} className='text-left'>
                            <H3 className='f-w-900 heading-color'>Current Properties</H3>
                        </Col>
                    </Row>
                    No properties have been added yet.
                </div>

                }

                <Row className='g-3 mt-4'>
                    <Col xs={6} className='mb-4 text-left'>
                        <H3 className='f-w-900 heading-color'>Assets - Property</H3>
                    </Col>
                    <Col xs={6} className="text-end">
                        <Btn color="primary" className='me-2' onClick={() => activeCallBack(3)} style={{ fontSize: '18px' }}>
                            Previous
                        </Btn>
                        <Button
                            type="button"
                            color="success"
                            onClick={() => { activeCallBack(5) }}
                            style={{ fontSize: '18px' }}
                        >
                            {Continue}
                        </Button>
                    </Col>
                </Row>

                

                <Row className="gx-2">
                    {/* Property Home Address */}
                    <Col sm={10} className='sm-row' >
                        <TextField
                            label="Home Address"
                            type="text"
                            name="propertyHomeAddress"
                            value={formData.propertyHomeAddress}
                            setValue={handleValueChange}
                            placeholder="Enter property home address"
                            // required={true}
                            error={formErrors.propertyHomeAddress}
                            errorMessage="Property home address is required"
                            setError={handleErrorChange}
                            design='flex'
                            maxChars={60}
                            passCol='col-8'
                            enableAddressFinder={true}
                            tabIndex={1}

                        />
                    </Col>

                    {/* Property Estimated Value */}
                    <Col sm={10} className='sm-row' >
                        <DollarInput
                            label="Estimated Value"
                            name="propertyEstimatedValue"
                            value={formData.propertyEstimatedValue}
                            setValue={handleValueChange}
                            placeholder="Enter estimated value"
                            // required={true}
                            limit={8}
                            error={formErrors.propertyEstimatedValue}
                            errorMessage="Property estimated value is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-3'
                            tabIndex={2}
                        />
                    </Col>

                    {/* Property Date Purchased */}
                    <Col sm={10} className='sm-row' >
                        <DatePicker
                            label="Date Purchased"
                            name="propertyDatePurchased"
                            value={formData.propertyDatePurchased}
                            setValue={handleValueChange as any}
                            // required={true}
                            error={formErrors.propertyDatePurchased}
                            errorMessage="Property date purchased is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-3'
                            tabIndex={3}
                        />
                    </Col>

                    {/* Property Type */}
                    <Col sm={10} className='sm-row' >
                        <DropDown
                            label="Property Type"
                            name="propertyType"
                            value={formData.propertyType}
                            setValue={handleValueChange}
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[9].dropDownValues : '')}
                            // required={true}
                            error={formErrors.propertyType}
                            errorMessage="Property type is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-3'
                            tabIndex={4}
                        />
                    </Col>

                    {/* Rental Income */}
                    <Col sm={10} className='sm-row' >
                        <DollarInput
                            label="Rental Income"
                            name="propertyRentalIncome"
                            value={formData.propertyRentalIncome}
                            setValue={handleValueChange}
                            placeholder="Enter rental income"
                            // required={true}
                            limit={5}
                            error={formErrors.propertyRentalIncome}
                            errorMessage="Property rental income is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-3'
                            tabIndex={5}
                        />
                    </Col>

                    {/* Property Rental Income DLL */}
                    <Col sm={10} className='sm-row' >
                        <DropDown
                            label="Frequency"
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[11].dropDownValues : '')}
                            name="propertyRentalIncomeDLL"
                            value={formData.propertyRentalIncomeDLL}
                            setValue={handleValueChange}
                            // required={true}
                            error={formErrors.propertyRentalIncomeDLL}
                            errorMessage="Property rental income DLL is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-3'
                            tabIndex={6}
                        />
                    </Col>

                    {/* Property Detailed Type */}
                    <Col sm={10} className='sm-row' >
                        <DropDown
                            label="Detailed Type"
                            name="propertyDetailedType"
                            value={formData.propertyDetailedType}
                            setValue={handleValueChange}
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[10].dropDownValues : '')}
                            // required={true}
                            error={formErrors.propertyDetailedType}
                            errorMessage="Property detailed type is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-3'
                            tabIndex={7}
                        />
                    </Col>

                    <Row className='g-3'>
                        <Col xs={12} className='mb-4 text-left'>
                            <H3 className='f-w-900 heading-color'>Loan Details</H3>
                        </Col>
                    </Row>

                    {/* Loan Details Borrow Type */}
                    <Col sm={10} className='sm-row' >
                        <DropDown
                            label="Borrow Type"
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[12].dropDownValues : '')}
                            name="loanDetailsBorrowTypeDLL"
                            value={formData.loanDetailsBorrowTypeDLL}
                            setValue={handleValueChange}
                            // required={true}
                            error={formErrors.loanDetailsBorrowTypeDLL}
                            errorMessage="Loan borrow type is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-4'
                            tabIndex={8}
                        />
                    </Col>

                    {/* Loan Details Current Mortgage */}
                    <Col sm={10} className='sm-row' >
                        <DollarInput
                            label="Current Mortgage"
                            name="loanDetailsCurrentMortgage"
                            value={formData.loanDetailsCurrentMortgage}
                            setValue={handleValueChange}
                            placeholder="Enter amount"
                            // required={true}
                            limit={8}
                            error={formErrors.loanDetailsCurrentMortgage}
                            errorMessage="Loan current mortgage is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-3'
                            tabIndex={9}
                        />
                    </Col>

                    {/* Loan Details Lender's Name */}
                    <Col sm={10} className='sm-row' >
                        <TextField
                            label="Lenders Name"
                            type="text"
                            name="loanDetailsLendersName"
                            value={formData.loanDetailsLendersName}
                            setValue={handleValueChange}
                            placeholder="Enter lender's name"
                            // required={true}
                            error={formErrors.loanDetailsLendersName}
                            errorMessage="Loan lenders name is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-4'
                            maxChars={20}
                            tabIndex={10}
                        />
                    </Col>

                    {/* Loan Details Repayment Type */}
                    <Col sm={10} className='sm-row' >
                        <DropDown
                            label="Repay Type"
                            name="loanDetailsRepayType"
                            value={formData.loanDetailsRepayType}
                            setValue={handleValueChange}
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[13].dropDownValues : '')}
                            // required={true}
                            error={formErrors.loanDetailsRepayType}
                            errorMessage="Loan repay type is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-4'
                            tabIndex={11}
                        />
                    </Col>

                    {/* Loan Details Repayment */}
                    {/* Repayment Field */}
                    <Col sm={10} className='sm-row' >
                        <DollarInput
                            label="Repayment"
                            name="loanDetailsRepayment"
                            value={formData.loanDetailsRepayment}
                            setValue={handleValueChange}
                            placeholder="Enter amount"
                            // required={true}
                            limit={5}
                            error={formErrors.loanDetailsRepayment}
                            errorMessage="Loan repay amount is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-3'
                            tabIndex={12}
                        />
                    </Col>

                    {/* Loan Details Repayment DLL Dropdown */}
                    <Col sm={10} className='sm-row' >
                        <DropDown
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[11].dropDownValues : '')}
                            label="Frequency"
                            name="loanDetailsRepaymentDLL"
                            value={formData.loanDetailsRepaymentDLL}
                            setValue={handleValueChange}
                            // required={true}
                            error={formErrors.loanDetailsRepaymentDLL}
                            errorMessage="Loan repay DLL is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-4'
                            tabIndex={13}
                        />
                    </Col>

                    {/* Loan Details Interest Rate */}
                    <Col sm={10} className='sm-row' >
                        <DecimalInput
                            label="Interest Rate"
                            name="loanDetailsInterestRate"
                            value={formData.loanDetailsInterestRate}
                            setValue={handleValueChange}
                            placeholder=""
                            // required={true}
                            limit={4}
                            error={formErrors.loanDetailsInterestRate}
                            errorMessage="Loan interest rate is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-2'
                            tabIndex={14}
                        />
                    </Col>

                    {/* Loan Details Interest Type */}
                    <Col sm={10} className='sm-row' >
                        <DropDown
                            label="Interest Type"
                            name="loanDetailsInterestType"
                            value={formData.loanDetailsInterestType}
                            setValue={handleValueChange}
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[14].dropDownValues : '')}
                            // required={true}
                            error={formErrors.loanDetailsInterestType}
                            errorMessage="Loan interest type is required"
                            setError={handleErrorChange}
                            design='flex'
                            passCol='col-4'
                            tabIndex={15}
                        />
                    </Col>
                </Row>
                <Button
                    disabled={isUpdateClientAssetsPending}
                    type="button"
                    color="primary"
                    onClick={handleSubmit}
                    style={{ fontSize: '18px' }}
                >
                    {isUpdateClientAssetsPending ? 'Loading . . .' : selectedRowId ? "Update Property" : "Add New Property"}
                </Button>

            </Form>
        </>
    );
}
